import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { ArrowLeftIcon, CheckIcon } from "@phosphor-icons/react";
import { createFileRoute, Link, redirect, useNavigate, useRouter } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import z from "zod";

import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { authClient } from "@/integrations/auth/client";

export const Route = createFileRoute("/auth/verify-2fa-backup")({
  component: RouteComponent,
  beforeLoad: async ({ context }) => {
    if (context.session) throw redirect({ to: "/dashboard", replace: true });
  },
});

const formSchema = z.object({
  code: z.string().trim(),
});

type FormValues = z.infer<typeof formSchema>;

function RouteComponent() {
  const router = useRouter();
  const navigate = useNavigate();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      code: "",
    },
  });

  const onSubmit = async (data: FormValues) => {
    const toastId = toast.loading(t`Verifying backup code...`);
    const formattedCode = `${data.code.slice(0, 5)}-${data.code.slice(5)}`;

    const { error } = await authClient.twoFactor.verifyBackupCode({ code: formattedCode });

    if (error) {
      toast.error(error.message, { id: toastId });
      return;
    }

    toast.dismiss(toastId);
    await router.invalidate();
    void navigate({ to: "/dashboard", replace: true });
  };

  return (
    <>
      <div className="space-y-1 text-center">
        <h1 className="text-2xl font-bold tracking-tight">
          <Trans>Verify with a Backup Code</Trans>
        </h1>
        <div className="text-muted-foreground">
          <Trans>Enter one of your saved backup codes to access your account</Trans>
        </div>
      </div>

      <Form {...form}>
        <form className="grid gap-6" onSubmit={form.handleSubmit(onSubmit)}>
          <FormField
            control={form.control}
            name="code"
            render={({ field }) => (
              <FormItem className="justify-self-center">
                <FormControl
                  render={<Input maxLength={10} value={field.value} onChange={field.onChange} className="max-w-xs" />}
                />
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex gap-x-2">
            <Button
              variant="outline"
              className="flex-1"
              nativeButton={false}
              render={
                <Link to="/auth/verify-2fa">
                  <ArrowLeftIcon />
                  <Trans>Go Back</Trans>
                </Link>
              }
            />

            <Button type="submit" className="flex-1">
              <CheckIcon />
              <Trans>Verify</Trans>
            </Button>
          </div>
        </form>
      </Form>
    </>
  );
}
